import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BACKEND_URL } from '../constants';
import {
  Folder,
  FoldersListResponse,
  CreateFolder,
  RenameFolder,
} from '@axys-notes/common';
import { useTeamsContext } from '../providers/teams-context-provider';
import { handleErrorResponse, handleRetry } from './common';

export const useFoldersQuery = () => {
  const teamsContext = useTeamsContext();
  return useQuery<FoldersListResponse>({
    enabled:
      !!teamsContext.authCode &&
      teamsContext.consent &&
      !teamsContext.consentInProgress,
    queryKey: ['folders'],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: async () => {
      const response = await fetch(`${BACKEND_URL}/api/folders`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: teamsContext.authCode
            ? `Bearer ${teamsContext.authCode}`
            : '',
        },
      });
      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    retry: (failureCount: number, error: Error) =>
      handleRetry(failureCount, error, teamsContext),
  });
};

export const useCreateFolderMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();
  return useMutation<Folder, Record<string, unknown>, CreateFolder>({
    mutationKey: ['create-folder'],
    mutationFn: async (data: CreateFolder) => {
      const response = await fetch(`${BACKEND_URL}/api/folders`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: teamsContext.authCode
            ? `Bearer ${teamsContext.authCode}`
            : '',
        },
        body: JSON.stringify(data),
      });

      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });
};

export const useRenameFolderMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();
  return useMutation<
    Folder,
    Record<string, unknown>,
    RenameFolder & { folderId: string }
  >({
    mutationKey: ['rename-folder'],
    mutationFn: async ({ folderId, ...data }) => {
      const response = await fetch(`${BACKEND_URL}/api/folders/${folderId}`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: teamsContext.authCode
            ? `Bearer ${teamsContext.authCode}`
            : '',
        },
        body: JSON.stringify(data),
      });

      await handleErrorResponse(response, teamsContext);
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });
};

export const useDeleteFolderMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();
  return useMutation<void, Record<string, unknown>, string>({
    mutationKey: ['delete-folder'],
    mutationFn: async (folderId: string) => {
      const response = await fetch(`${BACKEND_URL}/api/folders/${folderId}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: teamsContext.authCode
            ? `Bearer ${teamsContext.authCode}`
            : '',
        },
      });

      await handleErrorResponse(response, teamsContext);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folders'] });
      await queryClient.invalidateQueries({ queryKey: ['recording-list-get'] });
    },
  });
};

export const useAssignRecordingToFolderMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Record<string, unknown>,
    { folderId: string; recordingId: string }
  >({
    mutationKey: ['assign-recording-to-folder'],
    mutationFn: async ({ folderId, recordingId }) => {
      const response = await fetch(
        `${BACKEND_URL}/api/folders/${folderId}/recordings/${recordingId}`,
        {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
        }
      );

      await handleErrorResponse(response, teamsContext);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folders'] });
      await queryClient.invalidateQueries({ queryKey: ['recording-list-get'] });
    },
  });
};

export const useRemoveRecordingFromFolderMutation = () => {
  const teamsContext = useTeamsContext();
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Record<string, unknown>,
    { folderId: string; recordingId: string }
  >({
    mutationKey: ['remove-recording-from-folder'],
    mutationFn: async ({ folderId, recordingId }) => {
      const response = await fetch(
        `${BACKEND_URL}/api/folders/${folderId}/recordings/${recordingId}`,
        {
          method: 'DELETE',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: teamsContext.authCode
              ? `Bearer ${teamsContext.authCode}`
              : '',
          },
        }
      );

      await handleErrorResponse(response, teamsContext);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['folders'] });
      await queryClient.invalidateQueries({ queryKey: ['recording-list-get'] });
    },
  });
};
